import { Button } from '@insify/design-system/components';
import { Check, Plus, X } from '@insify/design-system/icons';
import { headingSm, headingXxs, textColor, textMd } from '@insify/design-system/typography';
import { useTranslationsFile } from '@insify/ui-translations';
import { interpolate } from '@insify/ui-utils';
import clsx from 'clsx';

import { actions, useActivity } from '../useActivity';

import * as styles from './index.module.css';

import type { Props } from '../ActivitySelector';
import type { InclusionOrExclusion } from '@insify/dto-company-policy';

const Card = ({
  heading,
  items,
  property,
}: {
  heading: string;
  items: InclusionOrExclusion[];
  property: 'inclusion' | 'exclusion';
}) => {
  return (
    <div className={styles.list}>
      <header className={headingXxs}>{heading}</header>
      <ul>
        {items.map((inclusionOrExclusion) => (
          <li key={inclusionOrExclusion.id} className={clsx(textMd, styles.listItem)}>
            {property === 'inclusion' ? <Check className={styles.check} /> : <X className={styles.cross} />}
            {inclusionOrExclusion[property as keyof typeof inclusionOrExclusion]}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Details = ({ translations, onSelect }: { translations: Props['translations']; onSelect: () => void }) => {
  const { state, dispatch } = useActivity();
  if (state.detailsActivityId === undefined) {
    return null;
  }
  const activityId = state.detailsActivityId;

  const activity = state.activityById[activityId];

  const copies = useTranslationsFile<{ details: string }>({
    translations: {
      details: interpolate(translations.details, { name: activity.title }),
    },
    sendErrorToSentry: true,
  });

  return (
    <>
      <header className={clsx(headingSm, styles.heading)}>{activity.title}</header>
      <p className={clsx(textMd, textColor, styles.paragraph)}>{copies.details}</p>
      {activity.inclusions !== undefined && activity.inclusions.length > 0 && (
        <Card heading={translations.inclusions} items={activity.inclusions} property='inclusion' />
      )}
      {activity.exclusions !== undefined && activity.exclusions.length > 0 && (
        <Card heading={translations.exclusions} items={activity.exclusions} property='exclusion' />
      )}
      <Button
        kind='primary'
        type='button'
        trackingId='save'
        className={styles.button}
        leftIcon={<Plus />}
        onClick={() => {
          dispatch(actions.activitySelectedInVertical(activityId));
          onSelect();
        }}
      >
        {translations.selectActivity}
      </Button>
    </>
  );
};
